import { useMemo, useEffect, useState, useRef } from 'react';
import axios from 'axios';
import {
  X, ChevronLeft, ChevronRight, Share2, Trash2, Sparkles, Archive, ArchiveRestore,
  CheckCircle2, GitBranch, Layers as LayersIcon, Info, Brush,
  ImageIcon, FileImage, Calendar, ShieldCheck, Cpu, Hash, Eye,
  ChevronDown, ChevronUp, Maximize2, Star, Wand2, ShieldX, Anchor, Scaling, Download, Scissors,
} from 'lucide-react';
import { HudCard, HudButton, HudBadge } from '../hud';
import RenderReviewPanel from './RenderReviewPanel';
import PlanCompareDialog from './PlanCompareDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

/** Prefix relative image URLs (`/api/lots/.../image`) with the backend host
 * so the browser hits the K8s ingress directly. Same helper as in the
 * gallery grid — kept local to avoid an import cycle. */
const fullImg = (path) => path ? `${process.env.REACT_APP_BACKEND_URL}${path}` : path;

/**
 * GalleryFocusView — V14.5 / Sprint D2 (Feb 2026).
 *
 * Mode Focus : remplace la grille classique quand l'utilisateur a cliqué sur
 * un rendu. Affiche :
 *  - une preview HD au centre,
 *  - un rail de miniatures cliquables en bas pour naviguer dans la liste filtrée,
 *  - un panel droit (360px) avec métadonnées + actions (valider, variation,
 *    partager, supprimer, annoter — D3).
 *
 * L'utilisateur peut quitter le mode focus via la croix ou la touche Escape.
 */
const GalleryFocusView = ({
  items,
  activeId,
  onChange,
  onClose,
  onShare,
  onValidate,
  onVariation,
  onCompare,
  onDelete,
  onArchive,
  onAnnotate,
  onSetCover,
  isCoverFor,
  onWhiteboxRef,
  onFidelity,
  onReviewChanged,
  onRelaunched,
  onUpscale,
  projectId,
}) => {
  const idx = useMemo(() => items.findIndex((it) => it.job_id === activeId), [items, activeId]);
  const active = idx >= 0 ? items[idx] : null;
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [planCompare, setPlanCompare] = useState(false); // V14.99 — fenêtre de comparaison au plan
  const [revisionMeta, setRevisionMeta] = useState(null); // V14.6 — Vision analysis for revisions
  const [revisionLoading, setRevisionLoading] = useState(false);
  const railRef = useRef(null);

  // Fetch revision metadata when the active item is a revision job
  useEffect(() => {
    if (!active || !projectId) { setRevisionMeta(null); return; }
    if (active.variation_category !== 'revision') { setRevisionMeta(null); return; }
    let cancelled = false;
    setRevisionLoading(true);
    axios.get(`${API}/projects/${projectId}/generations/${active.job_id}/revision-metadata`, opts)
      .then((r) => { if (!cancelled) setRevisionMeta(r.data); })
      .catch(() => { if (!cancelled) setRevisionMeta(null); })
      .finally(() => { if (!cancelled) setRevisionLoading(false); });
    return () => { cancelled = true; };
  }, [active, projectId]);

  // Keyboard nav: ESC closes, arrows navigate — désactivé quand la fenêtre de comparaison est ouverte.
  // Radix ferme le dialog sur Escape (event.preventDefault()) : on ignore cet événement
  // pour ne pas fermer aussi la vue focus (le listener se ré-attache pendant le bubbling).
  const escGuardRef = useRef(0);
  useEffect(() => {
    if (planCompare) { escGuardRef.current = Date.now(); return undefined; }
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (e.defaultPrevented || Date.now() - escGuardRef.current < 400) return;
        onClose();
        return;
      }
      if (e.key === 'ArrowLeft' && idx > 0) onChange(items[idx - 1].job_id);
      if (e.key === 'ArrowRight' && idx < items.length - 1) onChange(items[idx + 1].job_id);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [idx, items, onChange, onClose, planCompare]);

  // Scroll active thumb into view in the rail
  useEffect(() => {
    const el = railRef.current?.querySelector(`[data-thumb-id="${activeId}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [activeId]);

  if (!active) return null;

  const isVariation = !!active.parent_job_id;
  const completed = active.status === 'completed' && active.image_url;
  const validated = !!active.is_validated;

  return (
    <div className="space-y-4 hud-fade-in" data-testid="gallery-focus-view">
      <PlanCompareDialog open={planCompare} onClose={() => setPlanCompare(false)} item={active} projectId={projectId} />
      {/* Header : breadcrumb + exit */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-1 text-[11.5px] font-medium px-2.5 py-1.5 rounded-lg transition-colors"
            style={{
              background: 'var(--c-surface-2)', color: 'var(--c-text-2)',
              border: '1px solid var(--c-border)',
            }}
            data-testid="focus-close-btn"
          >
            <X className="w-3.5 h-3.5" />
            Retour à la galerie
          </button>
          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: 'var(--c-text-faint)' }}>
            {idx + 1} / {items.length}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => idx > 0 && onChange(items[idx - 1].job_id)}
            disabled={idx === 0}
            className="p-1.5 rounded-lg transition-colors disabled:opacity-30"
            style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)', color: 'var(--c-text)' }}
            data-testid="focus-prev-btn"
            aria-label="Précédent"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => idx < items.length - 1 && onChange(items[idx + 1].job_id)}
            disabled={idx >= items.length - 1}
            className="p-1.5 rounded-lg transition-colors disabled:opacity-30"
            style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)', color: 'var(--c-text)' }}
            data-testid="focus-next-btn"
            aria-label="Suivant"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Body : Preview + side panel */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
        {/* Preview */}
        <div
          className="relative rounded-xl overflow-hidden flex items-center justify-center"
          style={{
            background: 'var(--c-surface-2)',
            border: '1px solid var(--c-border)',
            minHeight: 480,
          }}
          data-testid="focus-preview"
        >
          {completed ? (
            <>
              <img
                src={fullImg(active.image_url)}
                alt={active.lot_number || active.job_id}
                className="max-h-[68vh] w-auto object-contain"
                style={{ display: 'block' }}
              />
              {/* V14.99 — comparaison au plan dans une fenêtre dédiée (remplace la superposition translucide) */}
              {active.lot_id && (
                <button
                  type="button"
                  onClick={() => setPlanCompare(true)}
                  className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-colors"
                  style={{
                    background: 'var(--c-gold)',
                    color: '#0F1115',
                    border: '1px solid var(--c-border-accent)',
                  }}
                  title="Comparer le rendu au plan / masque envoyé à l'IA (côte à côte, superposé, rideau)"
                  data-testid="focus-compare-plan-btn"
                >
                  <LayersIcon className="w-3 h-3" />
                  Comparer au plan
                </button>
              )}
            </>
          ) : (
            <div className="text-center p-10">
              <ImageIcon className="w-10 h-10 mx-auto mb-2" style={{ color: 'var(--c-text-muted)' }} />
              <p className="text-[13px]" style={{ color: 'var(--c-text-2)' }}>
                {active.status === 'pending'    ? 'En file d\'attente…' :
                 active.status === 'processing' ? 'Génération en cours…' :
                 active.status === 'failed'     ? 'Échec de la génération' :
                 'Image indisponible'}
              </p>
            </div>
          )}

          {/* Top-left flags */}
          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
            {validated && (
              <HudBadge tone="success">
                <CheckCircle2 className="w-3 h-3" /> Validé
              </HudBadge>
            )}
            {isVariation && (
              <HudBadge tone="gold">
                <GitBranch className="w-3 h-3" /> Variation
              </HudBadge>
            )}
            {active.config_source === 'materialtheque' && (
              <HudBadge tone="info">
                <ShieldCheck className="w-3 h-3" /> Matériauthèque
              </HudBadge>
            )}
          </div>
        </div>

        {/* Side panel */}
        <aside
          className="rounded-xl flex flex-col overflow-y-auto"
          style={{
            background: 'var(--c-surface-2)',
            border: '1px solid var(--c-border)',
            maxHeight: '68vh',
            minHeight: 0,
          }}
          data-testid="focus-side-panel"
        >
          {/* Identity */}
          <div className="p-4" style={{ borderBottom: '1px solid var(--c-border)' }}>
            <span className="hud-eyebrow hud-eyebrow-gold">Rendu</span>
            <p
              className="mt-1 font-semibold tracking-tight truncate"
              style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 16 }}
              title={active.lot_number || active.job_id}
            >
              {active.lot_number || `Rendu ${active.job_id.slice(-6)}`}
            </p>
            <p className="text-[11.5px] mt-0.5 truncate" style={{ color: 'var(--c-text-2)' }}>
              {active.building_name && `Bât. ${active.building_name}`}
              {active.floor_name && ` · ${active.floor_name}`}
              {active.typology && ` · ${active.typology}`}
            </p>
          </div>

          {/* Compact metadata */}
          <div
            className="px-4 py-3 space-y-1.5"
            style={{ borderBottom: '1px solid var(--c-border)' }}
            data-testid="focus-metadata"
          >
            <MetaRow icon={Calendar} label="Créé le" value={fmtDate(active.created_at)} />
            <MetaRow icon={Hash} label="Version" value={isVariation
              ? `v${(active.variation_index ?? '?')} · ${active.job_id.slice(-6)}`
              : `Original · ${active.job_id.slice(-6)}`}
            />
            {active.style_name && (
              <MetaRow icon={Sparkles} label="Style" value={active.style_name} accent="gold" />
            )}
            {active.params?.vue_angle && (
              <MetaRow icon={Eye} label="Vue" value={active.params.vue_angle} />
            )}
            {active.params?.render_stage && active.params.render_stage !== 'whitebox' && (
              <MetaRow icon={Cpu} label="Moteur" value={`${active.params.engine === 'gpt_image_1' ? 'GPT Image 1 (OpenAI)' : 'Nano Banana Pro (Google)'}${active.params.duel_id ? ' · Duel' : ''}`} accent={active.params.duel_id ? 'gold' : undefined} />
            )}
            {active.generation_time_seconds && (
              <MetaRow icon={Cpu} label="Durée IA" value={`${active.generation_time_seconds.toFixed(1)} s`} />
            )}
          </div>

          {/* V14.88 — Fidélité au plan (silhouette masque ↔ rendu) */}
          {(active.status === 'completed') && (
            <div className="px-4 py-3 space-y-1.5" style={{ borderBottom: '1px solid var(--c-border)' }}
                 data-testid="focus-fidelity-report">
              <div className="flex items-center justify-between gap-2">
                <span className="hud-eyebrow">Fidélité au plan</span>
                <div className="flex items-center gap-1.5">
                  {typeof active.fidelity_score === 'number' ? (
                    <span
                      className="text-[11px] font-bold tabular-nums px-2 py-0.5 rounded"
                      data-testid="focus-fidelity-score"
                      style={{
                        background: active.fidelity_score >= 80 ? 'rgba(34,197,94,0.18)'
                          : active.fidelity_score >= 60 ? 'rgba(251,191,36,0.16)' : 'rgba(244,63,94,0.18)',
                        color: active.fidelity_score >= 80 ? '#86EFAC'
                          : active.fidelity_score >= 60 ? '#FCD34D' : '#FDA4AF',
                      }}
                    >
                      {active.fidelity_score}%
                    </span>
                  ) : (
                    <span className="text-[10.5px]" style={{ color: 'var(--c-text-faint)' }}>non calculée</span>
                  )}
                  <button
                    type="button"
                    onClick={() => onFidelity?.(active)}
                    className="text-[10.5px] underline hover:opacity-80"
                    style={{ color: 'var(--c-text-muted)' }}
                    data-testid="focus-fidelity-recompute"
                  >
                    {typeof active.fidelity_score === 'number' ? 'recalculer' : 'calculer'}
                  </button>
                </div>
              </div>
              {active.fidelity_report?.scores && (
                <div className="grid grid-cols-4 gap-1 text-[10px]" data-testid="focus-fidelity-axes">
                  {[['outline', 'Contour'], ['walls', 'Murs'], ['stairs', 'Escalier'], ['openings', 'Ouvertures']].map(([k, l]) => (
                    <div key={k} className="rounded px-1.5 py-1 text-center" style={{ background: 'rgba(255,255,255,0.04)' }}>
                      <div style={{ color: 'var(--c-text-faint)' }}>{l}</div>
                      <div className="font-semibold tabular-nums" style={{ color: 'var(--c-text)' }}>{typeof active.fidelity_report.scores[k] === 'number' ? active.fidelity_report.scores[k] : '—'}</div>
                    </div>
                  ))}
                </div>
              )}
              {active.fidelity_report?.summary && (
                <p className="text-[11px] leading-snug" style={{ color: 'var(--c-text-2)' }}>{active.fidelity_report.summary}</p>
              )}
              {(active.fidelity_report?.issues || []).slice(0, 4).map((iss, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[10.5px] leading-snug" style={{ color: '#FCD34D' }}>
                  <span className="shrink-0 mt-0.5">⚠</span><span>{iss}</span>
                </div>
              ))}
            </div>
          )}

          {/* V14.92 — Notation / refus / annotation / relance */}
          <RenderReviewPanel
            item={active}
            projectId={projectId}
            onChanged={onReviewChanged}
            onRelaunched={onRelaunched}
            onAnnotate={onAnnotate}
          />

          {/* Phase 1 LangGraph — Rapport du Critic IA (conformité au plan) */}
          {typeof active.utility_score === 'number' && (
            <div className="px-4 py-3 space-y-1.5" style={{ borderBottom: '1px solid var(--c-border)' }}
                 data-testid="focus-critic-report">
              <div className="flex items-center justify-between">
                <span className="hud-eyebrow">Contrôle qualité IA</span>
                <span
                  className="text-[11px] font-bold tabular-nums px-2 py-0.5 rounded"
                  data-testid="focus-critic-score"
                  style={{
                    background: active.utility_score >= 80 ? 'rgba(34,197,94,0.18)'
                      : active.utility_score >= 55 ? 'rgba(251,191,36,0.16)' : 'rgba(244,63,94,0.18)',
                    color: active.utility_score >= 80 ? '#86EFAC'
                      : active.utility_score >= 55 ? '#FCD34D' : '#FDA4AF',
                  }}
                >
                  {active.utility_score}/100
                </span>
              </div>
              {active.critic_report?.summary && (
                <p className="text-[11px] leading-snug" style={{ color: 'var(--c-text-2)' }}>
                  {active.critic_report.summary}
                </p>
              )}
              {(active.critic_report?.issues || []).slice(0, 5).map((iss, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[10.5px] leading-snug"
                     style={{ color: iss.severity === 'major' ? '#FDA4AF' : '#FCD34D' }}>
                  <span className="shrink-0 mt-0.5">{iss.severity === 'major' ? '✕' : '⚠'}</span>
                  <span>{iss.detail}</span>
                </div>
              ))}
            </div>
          )}

          {/* Expandable detail */}
          <div style={{ borderBottom: '1px solid var(--c-border)' }}>
            <button
              type="button"
              onClick={() => setDetailsOpen((v) => !v)}
              className="w-full px-4 py-2.5 flex items-center justify-between text-left hover:bg-white/[0.03] transition-colors"
              data-testid="focus-details-toggle"
            >
              <span className="text-[11.5px] uppercase tracking-[0.16em] font-semibold" style={{ color: 'var(--c-text-2)' }}>
                Détails de génération
              </span>
              {detailsOpen
                ? <ChevronUp className="w-3.5 h-3.5" style={{ color: 'var(--c-text-muted)' }} />
                : <ChevronDown className="w-3.5 h-3.5" style={{ color: 'var(--c-text-muted)' }} />}
            </button>
            {detailsOpen && (
              <div className="px-4 pb-3 space-y-1.5 text-[11px]" style={{ color: 'var(--c-text-2)' }}>
                {Object.entries({
                  type_bien:       active.params?.type_bien,
                  ambiance:        active.params?.ambiance,
                  ambiance_extra:  active.params?.ambiance_extra,
                  lighting_mode:   active.params?.lighting_mode,
                  furniture_mode:  active.params?.furniture_mode,
                  scene_complement: active.params?.scene_complement,
                  config_source:   active.config_source,
                  model:           active.model_used,
                }).filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} className="flex items-start gap-2">
                    <span className="shrink-0 capitalize" style={{ color: 'var(--c-text-faint)' }}>
                      {k.replace(/_/g, ' ')} :
                    </span>
                    <span className="flex-1 break-words" style={{ color: 'var(--c-text)' }}>
                      {String(v)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Revision module — annotate & selective regeneration */}
          <div className="p-4 space-y-2" style={{ borderBottom: '1px solid var(--c-border)' }}>
            <span className="hud-eyebrow hud-eyebrow-gold">Revue & correction</span>
            <button
              type="button"
              onClick={() => onAnnotate?.(active)}
              disabled={!completed}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-[12.5px] font-semibold transition-all disabled:opacity-40"
              style={{
                background: 'linear-gradient(135deg, rgba(217,160,91,0.22), rgba(217,160,91,0.08))',
                color: 'var(--c-gold)',
                border: '1px solid var(--c-border-accent)',
                boxShadow: completed ? '0 0 16px -4px rgba(217,160,91,0.45)' : 'none',
              }}
              data-testid="focus-annotate-btn"
              title={completed ? 'Annoter l\'image et lancer une re-génération guidée' : 'Disponible une fois le rendu prêt'}
            >
              <Brush className="w-3.5 h-3.5" />
              Annoter & corriger
            </button>
            <p className="text-[10.5px] leading-relaxed" style={{ color: 'var(--c-text-faint)' }}>
              Dessinez ou textualisez les zones à modifier — l'IA conservera tout
              le reste à l'identique lors de la re-génération.
            </p>
          </div>

          {/* V14.6 — Revision metadata (Vision analysis transparency) */}
          {(active.variation_category === 'revision' || revisionMeta) && (
            <div className="p-4 space-y-2.5" style={{ borderBottom: '1px solid var(--c-border)' }} data-testid="focus-revision-meta">
              <div className="flex items-center justify-between gap-2">
                <span className="hud-eyebrow hud-eyebrow-gold inline-flex items-center gap-1.5">
                  <Wand2 className="w-3 h-3" /> Analyse IA de la révision
                </span>
                {revisionMeta?.parent_job_id && (
                  <button
                    type="button"
                    onClick={() => onChange?.(revisionMeta.parent_job_id)}
                    className="text-[10px] underline-offset-2 hover:underline"
                    style={{ color: 'var(--c-text-muted)' }}
                    data-testid="focus-revision-view-parent"
                  >
                    Voir l'original
                  </button>
                )}
              </div>

              {revisionLoading && (
                <p className="text-[11px]" style={{ color: 'var(--c-text-faint)' }}>
                  Chargement de l'analyse…
                </p>
              )}

              {!revisionLoading && revisionMeta?.revision_prompt && (
                <div className="rounded-md p-2" style={{ background: 'rgba(217,160,91,0.08)', border: '1px solid var(--c-border-accent)' }}>
                  <span className="block text-[9.5px] uppercase tracking-[0.16em] font-semibold mb-0.5" style={{ color: 'var(--c-gold)' }}>
                    Instruction
                  </span>
                  <p className="text-[11.5px] leading-relaxed" style={{ color: 'var(--c-text)' }}>
                    « {revisionMeta.revision_prompt} »
                  </p>
                </div>
              )}

              {!revisionLoading && revisionMeta?.vision_analysis && (
                <>
                  {revisionMeta.vision_analysis.modification_brief && (
                    <div className="rounded-md p-2" style={{ background: 'var(--c-surface-3)', border: '1px solid var(--c-border)' }}>
                      <span className="block text-[9.5px] uppercase tracking-[0.16em] font-semibold mb-0.5" style={{ color: 'var(--c-text-muted)' }}>
                        Brief reformulé par l'IA
                      </span>
                      <p className="text-[11.5px] leading-relaxed italic" style={{ color: 'var(--c-text-2)' }}>
                        {revisionMeta.vision_analysis.modification_brief}
                      </p>
                    </div>
                  )}

                  {(revisionMeta.vision_analysis.to_modify || []).length > 0 && (
                    <div>
                      <span className="text-[9.5px] uppercase tracking-[0.16em] font-semibold inline-flex items-center gap-1" style={{ color: '#FB923C' }}>
                        <Wand2 className="w-2.5 h-2.5" />
                        Modifications interprétées
                      </span>
                      <div className="flex flex-wrap gap-1 mt-1.5" data-testid="focus-revision-to-modify">
                        {revisionMeta.vision_analysis.to_modify.slice(0, 8).map((label, i) => (
                          <span
                            key={`mod-${i}`}
                            className="text-[10.5px] px-1.5 py-0.5 rounded"
                            style={{
                              background: 'rgba(251,146,60,0.10)',
                              border: '1px solid rgba(251,146,60,0.35)',
                              color: '#FB923C',
                            }}
                          >
                            {label}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {(revisionMeta.vision_analysis.to_preserve || []).length > 0 && (
                    <div>
                      <span className="text-[9.5px] uppercase tracking-[0.16em] font-semibold inline-flex items-center gap-1" style={{ color: '#86EFAC' }}>
                        <ShieldX className="w-2.5 h-2.5" />
                        Éléments préservés
                      </span>
                      <div className="flex flex-wrap gap-1 mt-1.5" data-testid="focus-revision-to-preserve">
                        {revisionMeta.vision_analysis.to_preserve.slice(0, 8).map((label, i) => (
                          <span
                            key={`pres-${i}`}
                            className="text-[10.5px] px-1.5 py-0.5 rounded"
                            style={{
                              background: 'rgba(34,197,94,0.10)',
                              border: '1px solid rgba(34,197,94,0.30)',
                              color: '#86EFAC',
                            }}
                          >
                            {label}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <p className="text-[9.5px] leading-relaxed" style={{ color: 'var(--c-text-faint)' }}>
                    Pas satisfait ? Cliquez sur <strong>Annoter & corriger</strong> pour
                    re-formuler ou affiner.
                  </p>
                </>
              )}

              {!revisionLoading && !revisionMeta?.vision_analysis && active.variation_category === 'revision' && (
                <p className="text-[11px] italic" style={{ color: 'var(--c-text-faint)' }}>
                  Analyse IA non disponible (révision encore en cours ou métadonnées absentes).
                </p>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="p-4 space-y-2">
            <span className="hud-eyebrow">Actions</span>
            {/* V15.29 — Livrable 4K (rendus validés) */}
            {completed && (() => {
              const up = active.upscale;
              const ready = up?.status === 'completed';
              const busy = up?.status === 'processing';
              return (
                <div className="rounded-md p-2.5 space-y-1.5" data-testid="focus-upscale-block"
                     style={{ background: ready ? 'rgba(217,160,91,0.10)' : 'var(--c-surface-3)', border: `1px solid ${ready ? 'var(--c-border-accent)' : 'var(--c-border)'}` }}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] uppercase tracking-[0.16em] font-semibold inline-flex items-center gap-1" style={{ color: ready ? 'var(--c-gold)' : 'var(--c-text-muted)' }}>
                      <Scaling className="w-3 h-3" /> Livrable 4K
                    </span>
                    <span className="text-[10.5px] tabular-nums" style={{ color: 'var(--c-text-2)' }} data-testid="focus-upscale-status">
                      {ready ? `${up.width}×${up.height}${up.cutout_path ? ' · détouré' : ''}` : busy ? 'en cours…' : up?.status === 'failed' ? 'échec' : validated ? 'non généré' : 'validez le rendu'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    <button type="button" onClick={() => validated && onUpscale?.(active)} disabled={!validated || busy}
                            data-testid="focus-action-upscale"
                            className="inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-[11px] font-semibold transition-colors disabled:opacity-30"
                            style={{ background: 'var(--c-gold)', color: '#0F1115' }}
                            title={validated ? 'Agrandir en 4K pour le livrable client (1920×1080 net garanti)' : 'Validez d’abord ce rendu'}>
                      <Scaling className="w-3 h-3" /> {ready ? 'Options 4K' : busy ? 'En cours…' : 'Agrandir en 4K'}
                    </button>
                    {ready ? (
                      <a href={`${API}/projects/${projectId}/generations/${active.job_id}/upscaled?variant=full&download=1`}
                         data-testid="focus-action-download-4k"
                         className="inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-[11px] font-medium"
                         style={{ background: 'var(--c-surface-3)', color: 'var(--c-text)', border: '1px solid var(--c-border)' }}>
                        <Download className="w-3 h-3" /> Télécharger 4K
                      </a>
                    ) : (
                      <span className="inline-flex items-center justify-center text-[10px] px-2" style={{ color: 'var(--c-text-faint)' }}>
                        {validated ? 'Déterministe ou affiné IA' : 'Réservé aux rendus validés'}
                      </span>
                    )}
                  </div>
                  {ready && up.cutout_path && (
                    <a href={`${API}/projects/${projectId}/generations/${active.job_id}/upscaled?variant=cutout&download=1`}
                       data-testid="focus-action-download-cutout"
                       className="w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-[10.5px] font-medium"
                       style={{ color: 'var(--c-text-2)', border: '1px dashed var(--c-border)' }}>
                      <Scissors className="w-3 h-3" /> PNG détouré (fond transparent)
                    </a>
                  )}
                </div>
              );
            })()}
            {/* V14.5 D2 — Set cover : highlighted gold button above the grid */}
            {active.lot_id && (
              <button
                type="button"
                onClick={() => completed && onSetCover?.(active)}
                disabled={!completed}
                data-testid="focus-action-set-cover"
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-[11.5px] font-semibold transition-all disabled:opacity-30"
                style={{
                  background: isCoverFor === active.job_id
                    ? 'rgba(217,160,91,0.22)'
                    : 'rgba(217,160,91,0.08)',
                  color: 'var(--c-gold)',
                  border: '1px solid var(--c-border-accent)',
                }}
                title="Épingle ce rendu comme image principale du lot (Plan de Vente, PDF, Vue Universelle)"
              >
                <Star
                  className="w-3.5 h-3.5"
                  fill={isCoverFor === active.job_id ? 'currentColor' : 'none'}
                />
                {isCoverFor === active.job_id ? 'Couverture du lot' : 'Définir comme couverture du lot'}
              </button>
            )}
            {['whitebox', 'textured', 'furnished'].includes(active.params?.render_stage) && (() => {
              const st = active.params?.render_stage;
              const isWb = st === 'whitebox';
              const isRef = isWb ? active.is_whitebox_reference : st === 'textured' ? active.is_textured_reference : active.is_furnished_reference;
              return (
                <button
                  type="button"
                  onClick={() => completed && onWhiteboxRef?.(active)}
                  disabled={!completed}
                  data-testid="focus-action-whitebox-ref"
                  className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-[11.5px] font-semibold transition-all disabled:opacity-30"
                  style={{
                    background: isRef ? 'rgba(244,114,182,0.22)' : 'rgba(244,114,182,0.08)',
                    color: '#F9A8D4',
                    border: '1px solid rgba(244,114,182,0.4)',
                  }}
                  title={isWb
                    ? 'Maquette blanche de référence du lot : l\'étape 2 (texturer) s\'y accroche'
                    : 'Rendu texturé de référence du lot : l\'étape 3 (meubler) s\'y accroche'}
                >
                  <Anchor className="w-3.5 h-3.5" />
                  {isRef
                    ? (isWb ? 'Maquette de référence du lot ✓' : st === 'textured' ? 'Texturé de référence du lot ✓' : 'Meublé de référence du lot ✓')
                    : (isWb ? 'Définir comme maquette de référence' : st === 'textured' ? 'Définir comme texturé de référence' : 'Définir comme meublé de référence')}
                </button>
              );
            })()}
            <div className="grid grid-cols-2 gap-1.5">
              <ActionBtn
                onClick={() => completed && onValidate?.(active)}
                disabled={!completed}
                icon={validated ? CheckCircle2 : ShieldCheck}
                label={validated ? 'Validation OK' : 'Valider'}
                tone={validated ? 'success' : 'default'}
                testid="focus-action-validate"
              />
              <ActionBtn
                onClick={() => completed && onVariation?.(active)}
                disabled={!completed}
                icon={GitBranch}
                label="Variation"
                testid="focus-action-variation"
              />
              <ActionBtn
                onClick={() => isVariation && completed && onCompare?.(active)}
                disabled={!isVariation || !completed}
                icon={LayersIcon}
                label="Comparer"
                testid="focus-action-compare"
              />
              <ActionBtn
                onClick={() => completed && onShare?.(active)}
                disabled={!completed}
                icon={Share2}
                label="Partager"
                testid="focus-action-share"
              />
              <ActionBtn
                onClick={() => completed && window.open(fullImg(active.image_url), '_blank')}
                disabled={!completed}
                icon={Maximize2}
                label="Plein écran"
                testid="focus-action-fullscreen"
              />
              <ActionBtn
                onClick={() => onArchive?.(active)}
                icon={active.is_archived ? ArchiveRestore : Archive}
                label={active.is_archived ? 'Désarchiver' : 'Archiver'}
                testid="focus-action-archive"
              />
              <ActionBtn
                onClick={() => onDelete?.(active)}
                icon={Trash2}
                label="Supprimer"
                tone="danger"
                testid="focus-action-delete"
              />
            </div>
          </div>
        </aside>
      </div>

      {/* Thumbnail rail */}
      <div
        ref={railRef}
        className="flex gap-2 overflow-x-auto py-2 -mx-1 px-1"
        style={{ scrollbarWidth: 'thin' }}
        data-testid="focus-thumb-rail"
      >
        {items.map((it) => {
          const isActive = it.job_id === activeId;
          const isChild = !!it.parent_job_id;
          return (
            <button
              key={it.job_id}
              data-thumb-id={it.job_id}
              data-testid={`focus-thumb-${it.job_id}`}
              type="button"
              onClick={() => onChange(it.job_id)}
              className="relative shrink-0 rounded-md overflow-hidden transition-all"
              style={{
                width: 84, height: 84,
                background: 'var(--c-surface-2)',
                border: `2px solid ${isActive ? 'var(--c-gold)' : 'var(--c-border)'}`,
                boxShadow: isActive ? '0 0 12px -2px rgba(217,160,91,0.5)' : 'none',
                opacity: isActive ? 1 : 0.78,
              }}
            >
              {it.image_url ? (
                <img
                  src={fullImg(it.image_url)}
                  alt={it.lot_number || it.job_id}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <FileImage className="w-4 h-4" style={{ color: 'var(--c-text-faint)' }} />
                </div>
              )}
              {isChild && (
                <span
                  className="absolute top-0.5 left-0.5 w-2 h-2 rounded-full"
                  style={{ background: 'var(--c-gold)', boxShadow: '0 0 4px rgba(217,160,91,0.7)' }}
                  title="Variation"
                />
              )}
              {it.is_validated && (
                <span
                  className="absolute bottom-0.5 right-0.5 w-2 h-2 rounded-full"
                  style={{ background: '#22C55E', boxShadow: '0 0 4px rgba(34,197,94,0.7)' }}
                  title="Validé"
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ---------------- Sub-components ---------------- */

const MetaRow = ({ icon: Icon, label, value, accent }) => (
  <div className="flex items-center gap-2 text-[11.5px]">
    <Icon className="w-3 h-3 shrink-0" style={{ color: accent === 'gold' ? 'var(--c-gold)' : 'var(--c-text-muted)' }} />
    <span className="shrink-0" style={{ color: 'var(--c-text-faint)' }}>{label}</span>
    <span
      className="flex-1 text-right truncate"
      style={{ color: accent === 'gold' ? 'var(--c-gold)' : 'var(--c-text)' }}
      title={value}
    >
      {value || '—'}
    </span>
  </div>
);

const ActionBtn = ({ icon: Icon, label, onClick, disabled, tone = 'default', testid }) => {
  const colors = tone === 'success'
    ? { bg: 'rgba(34,197,94,0.10)', color: '#86EFAC', border: 'rgba(34,197,94,0.3)' }
    : tone === 'danger'
    ? { bg: 'transparent', color: '#FCA5A5', border: 'rgba(239,68,68,0.25)' }
    : { bg: 'var(--c-surface-3)', color: 'var(--c-text)', border: 'var(--c-border)' };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-[11px] font-medium transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
      style={{ background: colors.bg, color: colors.color, border: `1px solid ${colors.border}` }}
    >
      <Icon className="w-3 h-3" />
      {label}
    </button>
  );
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: 'short', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso.slice(0, 16); }
};

export default GalleryFocusView;
